import dayjs, { Dayjs, isDayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';

import { DATE_FORMAT_DEFAULT, DATE_FORMAT_TO_SECOND } from '../constants';
import { TypeCodeState, TypeSelectOption } from '../types';

dayjs.locale('ko');

dayjs.extend(isBetweenPlugin);
dayjs.extend(isSameOrAfterPlugin);

/** @description number 타입 input에 숫자가 아닌 다른 문자는 입력되지 못하게 하는 함수 */
export const blockNonNumberKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e): void => {
  const regex = /^[0-9\b]$/;

  if (!regex.test(e.key) && e.key !== 'Backspace') {
    e.preventDefault();
  }
};

/** @description 선택된 옵션의 Text를 추출하는 함수 */
export const getSelectedOptionText = (
  value: string,
  options: TypeCodeState[] | null,
  defaultValue = '선택'
): string => {
  if (options === null || (Array.isArray(options) && options.length === 0)) {
    return defaultValue;
  }

  const selectedOptionText = options.find((option) => option.code === value)?.text;
  return selectedOptionText ?? defaultValue;
};

/** @description 날짜를 주어진 포맷대로 변경해주는 함수 */
export const getFormattedDate = ({
  date = dayjs(),
  format = DATE_FORMAT_DEFAULT,
  separator = '-',
}: {
  date?: string | Dayjs;
  format?: string;
  separator?: string;
} = {}): string => {
  dayjs.locale('ko');

  if (!date) {
    return '';
  }

  if (typeof separator === 'string' && separator !== '-') {
    format = format.replaceAll('-', separator);
  }

  if (isDayjs(date)) {
    return date.format(format);
  }

  return dayjs(date).format(format);
};

/** @description 숫자를 두자리 문자열 포맷으로 변경해주는 함수 */
export const formatNumberToTwoDigits = (number: number): string => {
  return number.toString().padStart(2, '0');
};

/** @description 주어진 스트링이 json 포맷의 문자열인지 체크해주는 함수 */
export const checkIsJsonString = (str: string): boolean => {
  try {
    const json = JSON.parse(str);
    return typeof json === 'object' && json !== null;
  } catch {
    return false;
  }
};

/** @description base64 문자열을 Blob 객체로 변환 */
export const base64ToBlob = (base64: string): Blob => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray]);
};

/** @description Blob 객체를 File 객체로 변환 */
export const base64ToFile = (base64: string, filename: string): File => {
  const blob = base64ToBlob(base64);
  return new File([blob], filename);
};

/** @description base64 문자열을 Byte로 반환 */
const getBase64FileSize = (base64String: string): number => {
  const base64Length = base64String.replace(/^data:image\/\w+;base64,/, '').length;
  return Math.ceil((base64Length * 3) / 4);
};

/** @description base64 문자열을 이미지화 및 maxFileSize 이하로 크기 조정 후 data URL로 반환 */
export const resizeBase64Img = (base64: string, maxFileSize: number): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const img = document.createElement('img');
    img.src = base64;
    img.onload = function () {
      if (!context) {
        resolve('');
        return;
      }

      let { width, height } = img;

      let resizedDataURL = '';
      let currentFileSize = getBase64FileSize(base64);

      while (currentFileSize > maxFileSize) {
        width /= 2;
        height /= 2;

        canvas.width = width;
        canvas.height = height;

        context.clearRect(0, 0, width, height);
        context.drawImage(img, 0, 0, width, height);

        resizedDataURL = canvas.toDataURL();
        currentFileSize = getBase64FileSize(resizedDataURL);
      }

      resolve(resizedDataURL || base64);
    };
  });
};

/** @description 계약 체결 시, 주민등록번호에 하이픈을 추가하는 함수 */
export const formatIdentificationNumber = (value: string): string => {
  return value
    .replace(/[^\d]/g, '')
    .replace(/(\d{6})(\d)/, '$1-$2')
    .slice(0, 14);
};

/** @description 주민등록번호에 하이픈을 추가하고 뒷자리 6자리를 *로 마스킹하는 함수 */
export const getMaskedJuminNo = (value: string): string => {
  return value ? value.replace(/(\d{6})(\d{1})(\d{6})/, '$1-$2******') : '';
};

/** @description 두 개의 문자열을 받아 같은 날짜인지 비교 후 결과 반환 */
export const isSameDate = (date1: string, date2: string): boolean | null => {
  if (!dayjs(date1).isValid() || !dayjs(date2).isValid()) {
    return null;
  }
  return dayjs(date1).isSame(dayjs(date2));
};

/** @description 객체 배열의 특정 키에 대해 중복을 제거하여 반환 */
export const getUniqueValues = <T>(array: T[], uniqueField: keyof T) => {
  const uniqueValues: Record<string, T[keyof T]> = {};

  array.forEach((item) => {
    const value = item?.[uniqueField];

    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      uniqueValues[value] = value;
    } else if (typeof value === 'object' && 'code' in value && typeof value.code === 'string') {
      uniqueValues[value.code] = value;
    }
  });

  return Object.values(uniqueValues);
};

/** @description 입력값이 숫자인 경우, 천 단위로 콤마를 찍어 문자열로 반환 */
export const addComma = (value: number | string, unit = '', returnValue = '-'): string => {
  if (typeof value === 'undefined' || value === null || Number.isNaN(Number(value))) {
    return returnValue;
  }
  return Number(value).toLocaleString().concat(unit);
};

/** @description 입력된 millisecond가 현재 시간과 얼마나 차이나는지 반환하는 함수 */
export const getDiffTime = (millisecond: number, unit: dayjs.QUnitType | dayjs.OpUnitType = 'minute'): number => {
  const currentTime = dayjs();
  const millisecondTime = dayjs(millisecond).format(DATE_FORMAT_TO_SECOND);

  return dayjs(millisecondTime).diff(currentTime, unit);
};

/**
 * @description 날짜가 입력된 날짜와 같거나 그 이후인지 확인 후 결과 반환
 * @example
 * isSameOrAfterDate('2025-08-18 10:00:00', '2025-08-18 09:00:00', 'second') // true
 */
export const isSameOrAfterDate = (date: string, targetDate: string, unit: dayjs.OpUnitType = 'day'): boolean => {
  return dayjs(date).isSameOrAfter(dayjs(targetDate), unit);
};

/** @description 날짜가 입력된 날짜보다 이후인지 확인 후 결과 반환 */
export const isAfterDate = (date: string, targetDate: string, unit: dayjs.OpUnitType = 'day'): boolean => {
  return dayjs(date).isAfter(dayjs(targetDate), unit);
};

/** @description 현재로부터 N일이 지났는지 여부를 반환하는 함수 */
export const checkOverNDays = (date: Dayjs | null, maxDay: number): boolean => {
  const today = dayjs();
  return today.diff(date, 'd') > maxDay;
};

/** @description 입력한 값이 유효한 날짜인지 반환하는 함수 */
export const isValidDay = (day: Dayjs | null): boolean => {
  return dayjs(day).isValid();
};

/** @description 근무 시간을 주어진 포맷으로 변경해주는 함수 */
export const getWorkingTimeText = (workingHour: number, workingMinute: number): string => {
  if (!workingHour || workingHour === 0) {
    return `${workingMinute}분`;
  }

  return `${workingHour}시간 ${workingMinute}분`;
};

/** @description 코드 타입을 셀렉트 컴포넌트 옵션으로 변경해주는 함수 */
export const formatterCodeToSelectOption = (option: TypeCodeState | null): TypeSelectOption | null => {
  if (!option) {
    return null;
  }
  return {
    value: option.code,
    text: option.text,
  };
};
